import { orgSelectors } from 'ducks/orgs'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { useSupplierCurrencySymbol } from 'hooks/useSupplierCurrencySymbol'
import {
  BaseTheme,
  Box,
  Button,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Icon,
  Snackbar,
  styled,
  Typography,
} from 'opensolar-ui'
import type { AddedToCartItemPayload } from 'pages/ordering/addedToCartToast/AddedToCartToastContext'
import {
  TOAST_VARIANT_ITEM,
  TOAST_VARIANT_PROJECT,
  useAddedToCartToast,
} from 'pages/ordering/addedToCartToast/AddedToCartToastContext'
import { useTranslate } from 'ra-core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { RootState } from 'types/state'
import { ActionType, EventType } from 'types/tracking'
import { currencySymbolForCountry, formatCurrencyWithSymbol } from 'util/misc'
import { computeLineItemDealPrice, filterProductLevelDeals } from '../deals/dealEngine'
import { Deal } from '../deals/types'

export const getLinePriceTotals = (line: {
  pricePerUnit?: number
  quantity?: number
  selectedDistributorOrderingData?: {
    deals?: Deal[]
    price_detail?: { discount_value?: number }
  }
}) => {
  const quantity = line.quantity ?? 0
  const unitPrice = line.pricePerUnit ?? 0
  const lineTotal = unitPrice * quantity
  if (!quantity || !unitPrice) return { totalPrice: 0, originalTotalPrice: null as number | null }

  const deals = filterProductLevelDeals((line.selectedDistributorOrderingData?.deals ?? []) as Deal[])
  if (deals.length) {
    const { adjustedTotal, totalDiscount } = computeLineItemDealPrice(unitPrice, quantity, deals)
    if (totalDiscount > 0) {
      return { totalPrice: adjustedTotal, originalTotalPrice: lineTotal }
    }
  }

  const tradeDiscount = line.selectedDistributorOrderingData?.price_detail?.discount_value ?? 0
  if (tradeDiscount > 0) {
    const originalTotal = (unitPrice / (1 - tradeDiscount)) * quantity
    if (originalTotal > lineTotal) {
      return { totalPrice: lineTotal, originalTotalPrice: originalTotal }
    }
  }

  return { totalPrice: lineTotal, originalTotalPrice: null }
}

const ToastPaper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: 8,
  boxShadow: theme.shadows[4],
  width: 336,
  overflow: 'hidden',
  pointerEvents: 'auto', // portal container has pointer-events: none; toast must receive clicks
}))

const ToastHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px 8px 16px',
}))

// Match contained Typography variant (body2SemiBold) – use variant color from theme or design token
const ToastTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: theme.palette.neutralGrey[900],
}))

const ToastBody = styled(Box)({
  padding: '0 16px 16px 16px',
})

const ToastItemRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  alignItems: 'flex-start',
  paddingTop: 12,
  paddingBottom: 12,
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}))

const ToastItemList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

// Match contained Typography variant (body3) – secondary; use variant color from theme or design token
const ToastMoreItems = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutralGrey[1300],
  marginTop: 4,
  paddingTop: 12,
  textAlign: 'center',
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}))

const ToastCartTotalRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
  marginTop: 12,
  paddingTop: 12,
  borderTop: `1px solid ${theme.palette.grey[300]}`,
  color: theme.palette.neutralGrey[1300],
}))

const ToastItemImage = styled('img')({
  width: 56,
  minWidth: 56,
  height: 56,
  objectFit: 'contain',
  borderRadius: 4,
  // backgroundColor: 'var(--mui-palette-grey-200, #eee)',
})

const EmptyImage = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56" fill="none">
    <rect width="56" height="56" rx="4" fill="#F1F1F1" />
  </svg>
)

const ToastItemContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
})

const ToastDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutralGrey[1100],
  marginBottom: 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const ToastDetailRow = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
}))

// Project BOM / Multi-project: Line 1 – short address
const ToastProjectLine1 = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2Medium,
  color: theme.palette.neutralGrey[1300],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  marginBottom: 4,
}))

// Project BOM / Multi-project: Line 2 – "Bill of Materials"
const ToastProjectLine2 = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2Medium,
  color: theme.palette.neutralGrey[900],
  display: 'block',
  marginBottom: 8,
}))

// Project BOM / Multi-project: Line 3 – Items count & Subtotal
const ToastProjectLine3Row = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
})

const ToastProjectLine3Text = styled(Typography)(({ theme }) => ({
  ...theme.typography.body3Medium,
  color: theme.palette.neutralGrey[1300],
}))

const ToastQuantityPriceRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 8,
})

// Match contained Typography variant (body3) – use variant color from theme or design token
const ToastDetailText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutralGrey[1300],
}))

const ToastStrikethroughPrice = styled(Typography)(({ theme }) => ({
  ...theme.typography.body3,
  color: theme.palette.grey[600],
  textDecoration: 'line-through',
}))

type ToastPriceDisplayProps = {
  totalPrice: number
  originalTotalPrice: number | null
  currencySymbol: string
}

const ToastPriceDisplay = ({ totalPrice, originalTotalPrice, currencySymbol }: ToastPriceDisplayProps) => {
  if (originalTotalPrice != null && originalTotalPrice > totalPrice) {
    return (
      <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body3Medium" colorHex={BaseTheme.palette.red[800]}>
          {formatCurrencyWithSymbol(totalPrice, currencySymbol)}
        </Typography>
        <ToastStrikethroughPrice variant="body3">
          {formatCurrencyWithSymbol(originalTotalPrice!, currencySymbol)}
        </ToastStrikethroughPrice>
      </Box>
    )
  }

  return <ToastDetailText variant="body3Medium">{formatCurrencyWithSymbol(totalPrice, currencySymbol)}</ToastDetailText>
}

const ToastActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 12,
  padding: '12px 16px',
  '& > *': { flex: 1 },
}))

const CloseIconButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.grey[600],
  '&:hover': {
    color: theme.palette.grey[800],
  },
}))

const TOAST_ALIGN_SELECTORS = ['#account-button', '#header-profile-area', '#main-header']

const measureToastRightPx = (): number | null => {
  for (const selector of TOAST_ALIGN_SELECTORS) {
    const el = document.querySelector(selector)
    if (el) {
      const rect = el.getBoundingClientRect()
      return window.innerWidth - rect.right
    }
  }
  return null
}

const TOAST_PORTAL_ID = 'toast-portal'

export const AddedToCartToast = () => {
  const translate = useTranslate()
  const history = useHistory()
  const location = useLocation()
  const { toastOpen, toastPayload, closeToast, onToastExited } = useAddedToCartToast()
  const { trackEvent } = useTrackComponent({
    componentKey: 'navigation_icon_buttons',
    eventName: 'Hardware Cart',
  })
  const previousPathnameRef = useRef<string | null>(null)
  const orgCountry = useSelector((state: any) => orgSelectors.getOrgIso2(state))
  const firstItemDistributor =
    toastPayload?.variant === TOAST_VARIANT_ITEM && toastPayload.items.length > 0
      ? (toastPayload.items[0].distributor ?? '')
      : ''
  const supplierSymbol = useSupplierCurrencySymbol(firstItemDistributor)
  const currencySymbol = supplierSymbol || currencySymbolForCountry(orgCountry)

  const [toastRightPx, setToastRightPx] = useState<number | null>(null)

  // Map of code -> { quantity, totalPrice } aggregated across all distributors for live cart state.
  const liveCodeMap = useSelector((state: RootState) => {
    if (!toastPayload || toastPayload.variant !== TOAST_VARIANT_ITEM) return {}
    const lineItems = state.orderComponents?.lineItems ?? []
    const codes = new Set(toastPayload.items.map((item: AddedToCartItemPayload) => item.code ?? ''))
    const map: Record<string, { quantity: number; totalPrice: number; originalTotalPrice: number }> = {}
    for (const line of lineItems) {
      if (!line.confirmed) continue
      const code = line.code ?? ''
      if (!codes.has(code)) continue
      if (!map[code]) map[code] = { quantity: 0, totalPrice: 0, originalTotalPrice: 0 }
      const { totalPrice, originalTotalPrice } = getLinePriceTotals(line as any)
      map[code].quantity += line.quantity ?? 0
      map[code].totalPrice += totalPrice
      map[code].originalTotalPrice += originalTotalPrice ?? totalPrice
    }
    return map
  })

  const [imageErrorKeys, setImageErrorKeys] = useState<Set<string>>(new Set())

  // Consolidate items by code: same component from different suppliers -> one row
  const consolidatedItems = useMemo(() => {
    if (!toastPayload || toastPayload.variant !== TOAST_VARIANT_ITEM) return []
    const map = new Map<
      string,
      {
        code: string
        description?: string
        imageUrl?: string
        quantity: number
        totalPrice: number
        originalTotalPrice: number
      }
    >()
    for (const item of toastPayload.items) {
      if (!item.code) continue
      const code = item.code
      const quantity = liveCodeMap[code]?.quantity ?? 0
      if (quantity <= 0) continue
      map.set(code, {
        code,
        description: item.description,
        imageUrl: item.imageUrl,
        quantity,
        totalPrice: liveCodeMap[code]?.totalPrice ?? 0,
        originalTotalPrice: liveCodeMap[code]?.originalTotalPrice ?? 0,
      })
    }
    return Array.from(map.values())
  }, [toastPayload, liveCodeMap])

  useEffect(() => {
    if (toastOpen && toastPayload?.variant === TOAST_VARIANT_ITEM && consolidatedItems.length === 0) {
      closeToast()
    }
  }, [consolidatedItems, toastOpen, toastPayload, closeToast])

  const firstItemCode =
    toastPayload?.variant === TOAST_VARIANT_ITEM && toastPayload.items.length > 0
      ? (toastPayload.items[0].code ?? '')
      : ''
  const firstItemLive = firstItemCode ? liveCodeMap[firstItemCode] : null
  const hasSeenLiveItemRef = useRef(false)

  // Total quantity of cart line items NOT represented in the toast (so we can show "+ N more item(s)")
  const otherItemsTotalQuantity = useSelector((state: any) => {
    if (!toastPayload || toastPayload.variant !== TOAST_VARIANT_ITEM) return 0
    const lineItems = state.orderComponents?.lineItems ?? []
    const representedCodes = new Set<string>()
    const seenCodes = new Set<string>()
    for (const item of toastPayload.items) {
      const code = item.code ?? ''
      if (seenCodes.has(code)) continue
      seenCodes.add(code)
      if (representedCodes.size < 3) representedCodes.add(code)
    }
    return lineItems.reduce((sum: number, line: { code: string; confirmed?: boolean; quantity?: number }) => {
      if (!line.confirmed) return sum
      if (representedCodes.has(line.code ?? '')) return sum
      return sum + (line.quantity ?? 0)
    }, 0)
  })

  const cartTotals = useSelector((state: any) => {
    if (!toastPayload) return { totalPrice: 0, originalTotalPrice: 0 }
    if (toastPayload.variant === TOAST_VARIANT_ITEM) {
      const lineItems = (state.orderComponents?.lineItems ?? []).filter(
        (line: { confirmed?: boolean }) => line.confirmed
      )
      return lineItems.reduce(
        (acc: { totalPrice: number; originalTotalPrice: number }, line: Parameters<typeof getLinePriceTotals>[0]) => {
          const { totalPrice, originalTotalPrice } = getLinePriceTotals(line)
          return {
            totalPrice: acc.totalPrice + totalPrice,
            originalTotalPrice: acc.originalTotalPrice + (originalTotalPrice ?? totalPrice),
          }
        },
        { totalPrice: 0, originalTotalPrice: 0 }
      )
    }
    if (toastPayload.variant === TOAST_VARIANT_PROJECT) {
      const totalPrice = toastPayload.projects.reduce(
        (sum: number, p: { subtotal: number }) => sum + (p.subtotal ?? 0),
        0
      )
      return { totalPrice, originalTotalPrice: totalPrice }
    }
    return { totalPrice: 0, originalTotalPrice: 0 }
  })

  useEffect(() => {
    if (firstItemLive) hasSeenLiveItemRef.current = true
  }, [firstItemLive])

  useEffect(() => {
    if (!toastOpen) hasSeenLiveItemRef.current = false
  }, [toastOpen])

  useEffect(() => {
    const pathname = location.pathname
    if (previousPathnameRef.current !== null && previousPathnameRef.current !== pathname && toastOpen) {
      closeToast()
    }
    previousPathnameRef.current = pathname
  }, [location.pathname, toastOpen, closeToast])

  useEffect(() => {
    if (toastPayload) setImageErrorKeys(new Set())
  }, [toastPayload])

  // Align toast right edge with profile dropdown: measure when toast opens and on resize
  useEffect(() => {
    if (!toastOpen || !toastPayload) return
    const update = () => {
      const right = measureToastRightPx()
      setToastRightPx(right)
    }
    update()
    const raf = requestAnimationFrame(update)
    const onResize = () => update()
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
  }, [toastOpen, toastPayload])

  const handleViewCart = useCallback(() => {
    trackEvent(EventType.USER_INTERACTION, { type: ActionType.CLICK }, { source: 'view_cart_button' })
    closeToast()
    history.push('/shop/cart')
  }, [closeToast, history])

  const handleCheckout = useCallback(() => {
    trackEvent(EventType.USER_INTERACTION, { type: ActionType.CLICK }, { source: 'checkout_button' })
    closeToast()
    history.push('/checkout')
  }, [closeToast, history])

  const handleClose = useCallback(() => {
    setImageErrorKeys(new Set())
    closeToast()
  }, [closeToast])

  const handleSnackbarClose = useCallback(
    (_event: React.SyntheticEvent, reason?: string) => {
      if (reason === 'clickaway' || reason === 'escapeKeyDown') return
      handleClose()
    },
    [handleClose]
  )

  const message = toastPayload ? (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <ToastPaper>
        <ToastHeader>
          <ToastTitleRow>
            <Typography variant="body2Medium">{translate('Added to your cart')}</Typography>
          </ToastTitleRow>
          <CloseIconButton type="button" onClick={handleClose} aria-label={translate('Close')}>
            <Icon icon="close" size="small" />
          </CloseIconButton>
        </ToastHeader>
        <ToastBody>
          {toastPayload.variant === TOAST_VARIANT_ITEM && (
            <ToastItemList>
              {consolidatedItems.slice(0, 3).map((item, index) => {
                const code = item.code
                return (
                  <ToastItemRow key={`${code}-${index}`}>
                    {item.imageUrl && !imageErrorKeys.has(code) ? (
                      <ToastItemImage
                        src={item.imageUrl}
                        alt=""
                        onError={() => {
                          setImageErrorKeys((prev) => new Set(prev).add(code))
                        }}
                      />
                    ) : (
                      <EmptyImage />
                    )}
                    <ToastItemContent>
                      {(item.description != null && item.description !== '') ||
                      (item.code != null && item.code !== '') ? (
                        <ToastDescription variant="body2">
                          {item.description != null && item.description !== '' ? item.description : item.code}
                        </ToastDescription>
                      ) : null}
                      <ToastQuantityPriceRow>
                        <ToastDetailText variant="body3Medium">
                          {translate('Quantity')}: {item.quantity}
                        </ToastDetailText>
                        <ToastPriceDisplay
                          totalPrice={item.totalPrice}
                          originalTotalPrice={item.originalTotalPrice}
                          currencySymbol={currencySymbol}
                        />
                      </ToastQuantityPriceRow>
                    </ToastItemContent>
                  </ToastItemRow>
                )
              })}
              {otherItemsTotalQuantity > 0 && (
                <ToastMoreItems variant="body3">
                  {otherItemsTotalQuantity === 1
                    ? translate('+ 1 more item')
                    : translate('+ %{count} more items', { count: otherItemsTotalQuantity })}
                </ToastMoreItems>
              )}
            </ToastItemList>
          )}
          {toastPayload.variant === TOAST_VARIANT_PROJECT && (
            <ToastItemList>
              {toastPayload.projects.slice(0, 3).map((project, index) => (
                <ToastItemRow key={`${project.label}-${index}`}>
                  <ToastItemContent>
                    <ToastProjectLine1 variant="body2Medium">{project.label}</ToastProjectLine1>
                    <ToastProjectLine2 variant="body2Medium">{translate('Bill of Materials')}</ToastProjectLine2>
                    {project.location === 'project_bom' ? (
                      <ToastQuantityPriceRow>
                        <ToastDetailText variant="body3Medium">
                          {translate('Quantity')}: {project.itemCount}
                        </ToastDetailText>
                        <ToastDetailText variant="body3Medium">
                          {formatCurrencyWithSymbol(project.subtotal, currencySymbol)}
                        </ToastDetailText>
                      </ToastQuantityPriceRow>
                    ) : (
                      <>
                        <ToastProjectLine3Row>
                          <ToastProjectLine3Text variant="body3Medium">
                            {translate('Items')}: {project.itemCount}
                          </ToastProjectLine3Text>
                          <ToastProjectLine3Text variant="body3Medium">
                            {translate('Subtotal')}: {formatCurrencyWithSymbol(project.subtotal, currencySymbol)}
                          </ToastProjectLine3Text>
                        </ToastProjectLine3Row>
                      </>
                    )}
                  </ToastItemContent>
                </ToastItemRow>
              ))}
              {toastPayload.projects.length > 3 && (
                <ToastMoreItems variant="body3">
                  {toastPayload.projects.length - 3 === 1
                    ? translate('+ 1 more project')
                    : translate('+ %{count} more projects', { count: toastPayload.projects.length - 3 })}
                </ToastMoreItems>
              )}
            </ToastItemList>
          )}
          <ToastCartTotalRow>
            <Typography variant="body2Medium">{translate('Cart total')}</Typography>
            <ToastPriceDisplay
              totalPrice={cartTotals.totalPrice}
              originalTotalPrice={cartTotals.originalTotalPrice}
              currencySymbol={currencySymbol}
            />
          </ToastCartTotalRow>
        </ToastBody>
        <ToastActions>
          <Button variant="outlined" color="default" size="small" onClick={handleViewCart}>
            {translate('View Cart')}
          </Button>
          <Button variant="contained" color="primary" size="small" onClick={handleCheckout}>
            {translate('Checkout')}
          </Button>
        </ToastActions>
      </ToastPaper>
    </ComponentVersionsInherit>
  ) : null

  const snackbar = (
    <Snackbar
      open={toastOpen && !!toastPayload}
      onClose={handleSnackbarClose}
      ClickAwayListenerProps={{ mouseEvent: false, touchEvent: false }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transitionDuration={{ enter: 200, exit: 500 }}
      TransitionProps={{ onExited: onToastExited }}
      style={{
        marginTop: 60,
        zIndex: 9999,
        ...(toastRightPx != null ? { right: toastRightPx } : {}),
      }}
      ContentProps={{ style: { padding: 0, backgroundColor: 'transparent', boxShadow: 'none' } as any }}
      message={message}
    />
  )

  const portalContainer =
    typeof document !== 'undefined' ? (document.getElementById(TOAST_PORTAL_ID) ?? document.body) : null
  return portalContainer ? createPortal(snackbar, portalContainer) : null
}
